import React, { useState } from "react";
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Delete as DeleteIcon, AccessTime as ClockIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { factory } from "../../../api/apiFactory";
import { useDispatch } from "react-redux";
import { showNotification } from "../../../app/slices/notificationSlice";

dayjs.extend(relativeTime);

const ReminderPopover = ({
  anchorPosition,
  open,
  onClose,
  reminders,
  onReminderDeleted,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deletingId, setDeletingId] = useState(null);

  const ticketRemindersApi = factory.get("ticketReminders");

  const handleReminderClick = (reminder) => {
    if (reminder.ticket?.id) {
      navigate(`/tickets/${reminder.ticket.id}`);
    }
    onClose();
  };

  const handleDeleteReminder = async (e, reminderId) => {
    e.stopPropagation();
    
    try {
      setDeletingId(reminderId);
      await ticketRemindersApi.delete(reminderId);
      
      dispatch(
        showNotification({
          type: "success",
          message: "Reminder deleted successfully",
        })
      );
      
      // Notify parent to refetch
      if (onReminderDeleted) {
        onReminderDeleted();
      }
    } catch (error) {
      dispatch(
        showNotification({
          type: "error",
          message: error?.response?.data?.message || "Failed to delete reminder",
        })
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          width: 400,
          maxHeight: 500,
          mt: 1,
        },
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          Ticket Reminders
        </Typography>
      </Box>
      <Divider />
      {!reminders || reminders.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <ClockIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No reminders set
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {reminders.map((reminder, index) => {
            const isDue = reminder.is_due;
            
            return (
              <React.Fragment key={reminder.id}>
                {index > 0 && <Divider />}
                <ListItem
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={(e) => handleDeleteReminder(e, reminder.id)}
                      disabled={deletingId === reminder.id}
                      size="small"
                    >
                      {deletingId === reminder.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => handleReminderClick(reminder)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      backgroundColor: isDue ? "#ffebee" : "#e8f5e9",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2" fontWeight="bold">
                            #{reminder.ticket?.number || "N/A"}
                          </Typography>
                          <Chip
                            label={reminder.ticket?.status || "unknown"}
                            size="small"
                            color={
                              reminder.ticket?.status === "open"
                                ? "success"
                                : reminder.ticket?.status === "closed"
                                ? "default"
                                : "warning"
                            }
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                          {isDue && (
                            <Chip
                              label="Due"
                              size="small"
                              color="error"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {reminder.ticket?.title || "Untitled ticket"}
                          </Typography>
                          {reminder.note && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontStyle: "italic", display: "block", mt: 0.5 }}
                            >
                              Note: {reminder.note}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color={isDue ? "error" : "text.secondary"}
                            sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
                          >
                            <ClockIcon sx={{ fontSize: 14 }} />
                            {dayjs(reminder.reminder_date).format("MMM D, YYYY h:mm A")}
                            {" · "}
                            {dayjs(reminder.reminder_date).fromNow()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Popover>
  );
};

export default ReminderPopover;
